import { useCallback, useMemo, useState } from "react";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import type { Nurse, NurseData } from "@/utils/models";
import type { NursesMatrixRowId } from "../NursesMatrixGrid/consts";
import { ADD_STAFF_ANALYTICS_SOURCE } from "./addStaffPublish";

const rowIdOf = (row: NurseData): NursesMatrixRowId => row.rowKey ?? row.id;

type Args = { weekIndex: number; dayNo: number; shiftNo: number };

/**
 * The staff picked in step 2, owned here and echoed back to the grid as `selectedRowIds`. The grid prunes
 * a pick that stops being selectable (a new shift window, a filter) and reports it, so this never keeps a
 * nurse the button would then add by mistake.
 */
export const useAddStaffSelection = ({ weekIndex, dayNo, shiftNo }: Args) => {
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const { track } = useAnalytics();
	const [selectedRowIds, setSelectedRowIds] = useState<NursesMatrixRowId[]>([]);
	const [selectedNurseIds, setSelectedNurseIds] = useState<number[]>([]);

	const onSelectionChange = useCallback(
		(rows: NurseData[]) => {
			const added = rows.find((row) => !selectedRowIds.includes(rowIdOf(row)));
			setSelectedRowIds(rows.map(rowIdOf));
			setSelectedNurseIds(rows.map((row) => row.id));
			if (!added) return;
			const nurse = getNurseById(added.id);
			track("Staffing - select nurse", {
				source: ADD_STAFF_ANALYTICS_SOURCE,
				week_index: weekIndex,
				day_no: dayNo,
				shift_no: shiftNo,
				nurse_id: added.id,
				nurse_name: added.name,
				nurse_role: nurse?.role,
				selected_count: rows.length,
			});
		},
		[selectedRowIds, getNurseById, weekIndex, dayNo, shiftNo, track],
	);

	const selectedNurses = useMemo(
		() => selectedNurseIds.map((id) => getNurseById(id)).filter((nurse): nurse is Nurse => nurse !== undefined),
		[selectedNurseIds, getNurseById],
	);

	return { selectedRowIds, selectedNurses, onSelectionChange };
};
